import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePurchaseOrders, useDeletePurchaseOrder } from '@hooks/useProcurement'

const statusColors: Record<string, string> = {
  DRAFT: 'badge-warning',
  SENT: 'badge-info',
  CONFIRMED: 'badge-success',
  PARTIALLY_RECEIVED: 'badge-warning',
  FULLY_RECEIVED: 'badge-success',
  CANCELLED: 'badge-danger',
}

const statusLabels: Record<string, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  CONFIRMED: 'Confirmed',
  PARTIALLY_RECEIVED: 'Partially Received',
  FULLY_RECEIVED: 'Fully Received',
  CANCELLED: 'Cancelled',
}

export function PurchaseOrderListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = usePurchaseOrders(search, status)
  const delete_ = useDeletePurchaseOrder()

  const orders = data?.data?.items || []
  const total = data?.data?.total || 0
  const pageSize = data?.data?.pageSize || 20
  const totalPages = data?.data?.totalPages || 0

  const handleDelete = (id: number) => {
    if (
      window.confirm(
        'Are you sure you want to delete this purchase order? This can only be done for draft orders.'
      )
    ) {
      delete_.mutate(id)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-gray-600 text-sm mt-1">
            Place orders to suppliers before goods arrive
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/procurement/purchase-orders/create')}
          className="btn-primary"
        >
          + Create Purchase Order
        </button>
      </div>

      <div className="card">
        <div className="space-y-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Search by PO number..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full"
            />

            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(1)
              }}
              className="w-full"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="SENT">Sent</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="PARTIALLY_RECEIVED">Partially Received</option>
              <option value="FULLY_RECEIVED">Fully Received</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            Failed to load purchase orders. Please try again.
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No purchase orders found.' : 'No purchase orders yet.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      PO Number
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Supplier
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Order Date
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Expected Delivery
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Items
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Status
                    </th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 font-mono font-medium">
                        {order.poNumber}
                      </td>
                      <td className="px-4 py-3 text-sm">{order.supplier.name}</td>
                      <td className="px-4 py-3 text-sm">
                        {new Date(order.orderDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {new Date(
                          order.expectedDeliveryDate
                        ).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-center font-medium">
                        {order.items?.length || 0}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${statusColors[order.status]}`}>
                          {statusLabels[order.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              navigate(
                                `/dashboard/procurement/purchase-orders/${order.id}`
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            View
                          </button>
                          {order.status === 'DRAFT' && (
                            <button
                              onClick={() => handleDelete(order.id)}
                              disabled={delete_.isPending}
                              className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
